import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";
import Button from "../../componentes/button/button";
import MainCard from "../../componentes/card/MainCard";
import TiInventarioService from "../../services/resource/tiInventarioService";
import { FASE_ESTILO } from "./tiUtils";

const service = new TiInventarioService();

const ABAS = [
    { tipo: "STATUS", rotulo: "Status" },
    { tipo: "DEPARTAMENTO", rotulo: "Departamentos" },
    { tipo: "TIPO_DISPOSITIVO", rotulo: "Tipos de dispositivo" },
    { tipo: "MARCA", rotulo: "Marcas" },
    { tipo: "MODELO", rotulo: "Modelos" },
    { tipo: "OPERADORA", rotulo: "Operadoras" },
];

const INPUT = "border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-full";

export default function CatalogosTiPage() {
    document.title = "MyDash | Cadastros do Inventário de TI";
    const navigate = useNavigate();

    const [tipo, setTipo] = useState("STATUS");
    const [itens, setItens] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [metadados, setMetadados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [edicao, setEdicao] = useState(null);
    const [salvando, setSalvando] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const res = await service.listarCatalogo(tipo, { comUso: true });
            setItens(res.data || []);
        } finally {
            setCarregando(false);
        }
    }, [tipo]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        service.metadados().then(res => setMetadados(res.data));
        service.listarCatalogo("MARCA", { somenteAtivos: true }).then(res => setMarcas(res.data || []));
    }, []);

    const abrirNovo = () => setEdicao({ tipo, nome: "", ativo: true, paiId: null, categoria: null, fase: null });

    const salvar = async () => {
        if (!(edicao.nome || "").trim()) {
            toast.error("Informe o nome.");
            return;
        }
        setSalvando(true);
        try {
            const payload = {
                tipo: edicao.tipo, nome: edicao.nome, ativo: edicao.ativo,
                paiId: edicao.paiId, categoria: edicao.categoria, fase: edicao.fase,
            };
            if (edicao.id) await service.atualizarCatalogo(edicao.id, payload);
            else await service.cadastrarCatalogo(payload);
            toast.success("Cadastro salvo com sucesso!");
            setEdicao(null);
            await carregar();
            if (tipo === "MARCA") {
                service.listarCatalogo("MARCA", { somenteAtivos: true }).then(res => setMarcas(res.data || []));
            }
        } catch (e) {
            // validação do backend exibida pelo interceptor
        } finally {
            setSalvando(false);
        }
    };

    const alternarAtivo = async (item) => {
        await service.alterarAtivoCatalogo(item.id, !item.ativo);
        toast.success(item.ativo ? "Cadastro inativado." : "Cadastro reativado.");
        await carregar();
    };

    const excluir = async (item) => {
        if (!window.confirm(`Excluir "${item.nome}"? Só é possível quando o cadastro nunca foi utilizado.`)) return;
        try {
            await service.excluirCatalogo(item.id);
            toast.success("Cadastro excluído.");
            await carregar();
        } catch (e) {
            // mensagem do backend orienta a inativar
        }
    };

    const categorias = (metadados?.categorias || []).map(c => ({ value: c.codigo, label: c.rotulo }));
    const fases = (metadados?.fases || []).map(f => ({ value: f.valor, label: f.rotulo }));

    return (
        <MainCard
            title="Cadastros auxiliares do Inventário de TI"
            icon="fa fa-sliders-h"
            header={
                <div className="flex gap-2">
                    <Button label="Voltar ao inventário" iconLeft="fa fa-arrow-left" variant="gray" onClick={() => navigate("/ti/inventario")} />
                    <Button label="Novo" iconLeft="fa fa-plus" variant="indigo" onClick={abrirNovo} />
                </div>
            }
        >
            <div className="px-6 pt-4 flex flex-wrap gap-1 border-b border-border">
                {ABAS.map(a => (
                    <button key={a.tipo} onClick={() => setTipo(a.tipo)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tipo === a.tipo
                            ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        {a.rotulo}
                    </button>
                ))}
            </div>

            <div className="p-6">
                <p className="text-xs text-muted-foreground mb-3">
                    Itens já usados por ativos não podem ser excluídos — inative-os para que deixem de ser oferecidos nos
                    formulários, preservando o histórico.
                </p>
                <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/60 text-left">
                            <tr>
                                <th className="px-3 py-2">Nome</th>
                                {tipo === "MODELO" && <th className="px-3 py-2">Marca</th>}
                                {tipo === "TIPO_DISPOSITIVO" && <th className="px-3 py-2">Categoria</th>}
                                {tipo === "STATUS" && <th className="px-3 py-2">Fase do ciclo de vida</th>}
                                <th className="px-3 py-2">Em uso</th>
                                <th className="px-3 py-2">Situação</th>
                                <th className="px-3 py-2 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {itens.map(i => (
                                <tr key={i.id} className="border-t border-border">
                                    <td className="px-3 py-2 font-medium">{i.nome}</td>
                                    {tipo === "MODELO" && <td className="px-3 py-2">{i.paiNome}</td>}
                                    {tipo === "TIPO_DISPOSITIVO" && <td className="px-3 py-2">{i.categoriaRotulo}</td>}
                                    {tipo === "STATUS" && (
                                        <td className="px-3 py-2">
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${FASE_ESTILO[i.fase] || ""}`}>{i.faseRotulo}</span>
                                        </td>
                                    )}
                                    <td className="px-3 py-2">{i.emUso ?? 0}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${i.ativo ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                                            {i.ativo ? "Ativo" : "Inativo"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <button className="text-primary hover:underline mr-3" onClick={() => setEdicao({ ...i })}>Editar</button>
                                        <button className="hover:underline mr-3" onClick={() => alternarAtivo(i)}>{i.ativo ? "Inativar" : "Reativar"}</button>
                                        <button className="text-destructive hover:underline" disabled={i.emUso > 0} onClick={() => excluir(i)}>Excluir</button>
                                    </td>
                                </tr>
                            ))}
                            {!carregando && itens.length === 0 && (
                                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Nenhum cadastro encontrado.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog
                header={edicao?.id ? "Editar cadastro" : "Novo cadastro"}
                visible={!!edicao}
                onHide={() => setEdicao(null)}
                blockScroll
                className="w-[95%] md:w-[480px]"
                contentClassName="pt-3"
            >
                {edicao && (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs text-muted-foreground mb-1">Nome *</label>
                            <input className={INPUT} value={edicao.nome} onChange={e => setEdicao(p => ({ ...p, nome: e.target.value }))} />
                        </div>
                        {tipo === "MODELO" && (
                            <div>
                                <label className="block text-xs text-muted-foreground mb-1">Marca *</label>
                                <Dropdown className="w-full text-sm" filter value={edicao.paiId ?? null}
                                    options={marcas.map(m => ({ value: m.id, label: m.nome }))}
                                    onChange={e => setEdicao(p => ({ ...p, paiId: e.value }))} placeholder="Selecione a marca" />
                            </div>
                        )}
                        {tipo === "TIPO_DISPOSITIVO" && (
                            <div>
                                <label className="block text-xs text-muted-foreground mb-1">Categoria *</label>
                                <Dropdown className="w-full text-sm" value={edicao.categoria ?? null} options={categorias}
                                    onChange={e => setEdicao(p => ({ ...p, categoria: e.value }))} placeholder="Selecione a categoria" />
                                <p className="text-xs text-muted-foreground mt-1">
                                    A categoria define os campos específicos exibidos no cadastro do ativo.
                                </p>
                            </div>
                        )}
                        {tipo === "STATUS" && (
                            <div>
                                <label className="block text-xs text-muted-foreground mb-1">Fase do ciclo de vida *</label>
                                <Dropdown className="w-full text-sm" value={edicao.fase ?? null} options={fases}
                                    onChange={e => setEdicao(p => ({ ...p, fase: e.value }))} placeholder="Selecione a fase" />
                                <p className="text-xs text-muted-foreground mt-1">
                                    As regras (transições, responsável exigido, baixa) seguem a fase, não o nome do status.
                                </p>
                            </div>
                        )}
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={edicao.ativo} onChange={e => setEdicao(p => ({ ...p, ativo: e.target.checked }))} />
                            Ativo (disponível nos formulários)
                        </label>
                        <div className="flex justify-end gap-2 pt-3 border-t border-border">
                            <Button label="Cancelar" variant="gray" onClick={() => setEdicao(null)} />
                            <Button label="Salvar" variant="indigo" loading={salvando} onClick={salvar} />
                        </div>
                    </div>
                )}
            </Dialog>
        </MainCard>
    );
}
